import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Dropdown } from "react-bootstrap";
import { useSelector } from "react-redux";
import {
  FaChevronLeft,
  FaChevronRight,
  FaChevronDown,
  FaRegClock,
  FaLock,
  FaLayerGroup,
  FaTrashAlt,
  FaTimes,
  FaPlus,
  FaMinus,
  FaSave,
  FaExclamationTriangle,
  FaInbox,
  FaSpinner,
  FaArrowRight,
  FaBolt,
  FaCopy,
} from "react-icons/fa";
import { useGetMetrosQuery } from "../../../api/metro";
import { useGetStoresQuery } from "../../../api/stores";
import { useGetShiftTemplatesQuery } from "../../../api/shiftTemplate";
import {
  useGetSchedulesByMetroQuery,
  useUpsertScheduleMutation,
  useRunShiftsFromScheduleMutation,
} from "../../../api/shiftDailySchedule";
import { useGetZonesQuery } from "../../../api/zones";
import CardLayout from "../../../components/layouts/CardLayout";
import ConfirmDialog from "../../../components/shared/customModal/confirmDialog";
import "./style.scss";

const DAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

// Hub pill dot color reflects weekly coverage (created vs. planned shifts):
// green = fully staffed, amber = nearly there, red = understaffed, magenta = overbooked.
const HUB_TONE_COLORS = { good: "#2fb344", warn: "#f2a93b", bad: "#e5484d", over: "#c026d3", neutral: "#9aa5b8" };

const getWeekMonday = (offset = 0) => {
  const today = new Date();
  const todayDay = today.getDay();
  const monday = new Date(today);
  monday.setDate(today.getDate() - ((todayDay + 6) % 7) + offset * 7);
  monday.setHours(0, 0, 0, 0);
  return monday;
};

const toDateString = (date) => date.toISOString().split("T")[0];

let idCounter = 0;
const uid = (prefix) => {
  idCounter += 1;
  return `${prefix}_${Date.now().toString(36)}_${idCounter}`;
};

const getScheduleForDate = (hubSchedules, dateStr) =>
  hubSchedules.find((s) => {
    const sDate = s.scheduleDate ? new Date(s.scheduleDate) : null;
    return sDate && toDateString(sDate) === dateStr;
  });

// Best-effort "7:00 AM" / "19:00" -> minutes-of-day parser, used only to sort
// time groups chronologically. Unparseable values sort to the end.
const parseTimeToMinutes = (value) => {
  const str = String(value || "").trim();
  const match = str.match(/^(\d{1,2}):?(\d{2})?\s*(am|pm)?$/i);
  if (!match) return Infinity;
  let hours = parseInt(match[1], 10);
  const minutes = match[2] ? parseInt(match[2], 10) : 0;
  const meridiem = match[3]?.toLowerCase();
  if (meridiem === "pm" && hours < 12) hours += 12;
  if (meridiem === "am" && hours === 12) hours = 0;
  return hours * 60 + minutes;
};

const EDITABILITY_LEAD_MS = 2 * 60 * 60 * 1000;

const getSlotDateTime = (dayDate, startTimeStr) => {
  const minutes = parseTimeToMinutes(startTimeStr);
  if (!Number.isFinite(minutes)) return null;
  const slot = new Date(dayDate);
  slot.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);
  return slot;
};

// A cell is locked until that day + start time is at least 2 hours from now.
const isScheduleSlotLocked = (dayDate, startTimeStr) => {
  const slotDateTime = getSlotDateTime(dayDate, startTimeStr);
  if (!slotDateTime) return false;
  return slotDateTime.getTime() < Date.now() + EDITABILITY_LEAD_MS;
};

const isPastScheduleDay = (dayDate) => {
  const dayStart = new Date(dayDate);
  dayStart.setHours(0, 0, 0, 0);
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  return dayStart.getTime() < todayStart.getTime();
};

// Free-typed formats like "7:00 AM", "7:10 AM", "2 PM" are all valid — this
// only reformats them to a consistent "h:mm AM/PM" string, used to normalize
// on blur without forcing a rigid input mask while typing.
const normalizeTimeInput = (value) => {
  const minutes = parseTimeToMinutes(value);
  if (!Number.isFinite(minutes)) return String(value || "").trim();
  let hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const meridiem = hours >= 12 ? "PM" : "AM";
  hours %= 12;
  if (hours === 0) hours = 12;
  return `${hours}:${String(mins).padStart(2, "0")} ${meridiem}`;
};

// Suggestions offered via <datalist> so the field still feels like a time
// picker, but any of "7:00 AM" / "7:10 AM" / "2 PM" typed by hand also works.
const TIME_SUGGESTIONS_ID = "scheduling-time-suggestions";
const TIME_SUGGESTIONS = Array.from({ length: 24 * 4 }, (_, i) => {
  let hours = Math.floor(i / 4);
  const minutes = (i % 4) * 15;
  const meridiem = hours >= 12 ? "PM" : "AM";
  hours %= 12;
  if (hours === 0) hours = 12;
  return `${hours}:${String(minutes).padStart(2, "0")} ${meridiem}`;
});

const ZoneMultiSelect = ({ value, onChange, disabled, hasError, zoneOptions = [], isLoading = false }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [dropdownStyle, setDropdownStyle] = useState({});
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  const calcDropdownStyle = () => {
    if (!inputRef.current) return {};
    const rect = inputRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const maxHeight = Math.min(240, Math.max(spaceBelow, spaceAbove) - 8);
    const openBelow = spaceBelow >= spaceAbove || spaceBelow >= 120;
    return {
      position: "fixed",
      left: rect.left,
      width: rect.width,
      zIndex: 9999,
      maxHeight,
      overflowY: "auto",
      ...(openBelow
        ? { top: rect.bottom + 4 }
        : { bottom: window.innerHeight - rect.top + 4 }),
    };
  };

  useEffect(() => {
    const handleClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => {
    if (!open) return;
    const close = (e) => {
      if (dropdownRef.current && dropdownRef.current.contains(e.target)) return;
      setOpen(false);
      setSearch("");
    };
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open]);

  const handleFocus = () => {
    if (disabled) return;
    setDropdownStyle(calcDropdownStyle());
    setOpen(true);
  };

  const toggle = (zone) => {
    if (value.includes(zone)) {
      onChange(value.filter((z) => z !== zone));
    } else {
      onChange([...value, zone].sort());
    }
  };

  const filtered = search.trim()
    ? zoneOptions.filter((z) => z.toLowerCase().includes(search.toLowerCase()))
    : zoneOptions;

  const placeholder = isLoading
    ? "Loading zones..."
    : value.length === 0
    ? "Select zones"
    : value.join(", ");

  return (
    <div
      ref={wrapRef}
      className={[
        "zone-multiselect",
        open ? "open" : "",
        hasError ? "is-invalid" : "",
        disabled ? "disabled" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="zone-multiselect__trigger">
        <input
          ref={inputRef}
          type="text"
          className="zone-multiselect__search-input"
          value={open ? search : ""}
          placeholder={placeholder}
          onChange={(e) => setSearch(e.target.value)}
          onFocus={handleFocus}
          disabled={disabled}
          autoComplete="off"
          aria-haspopup="listbox"
          aria-expanded={open}
        />
        <span className="zone-multiselect__arrow">▾</span>
      </div>
      {open && (
        <div ref={dropdownRef} className="zone-multiselect__dropdown" role="listbox" style={dropdownStyle}>
          {filtered.length === 0 ? (
            <span className="zone-multiselect__option zone-multiselect__option--empty">
              No zones found
            </span>
          ) : (
            filtered.map((z) => (
              <label key={z} className="zone-multiselect__option">
                <input
                  type="checkbox"
                  checked={value.includes(z)}
                  onChange={() => toggle(z)}
                />
                {z}
              </label>
            ))
          )}
        </div>
      )}
    </div>
  );
};

const zoneKey = (zoneNames) => [...zoneNames].sort().join("||");

const SERVER_ROUTE_TYPE_BT = "BT";
const SERVER_ROUTE_TYPE_UBER = "UBER DIRECT";

// A zone row now only ever carries a BT count — Uber Direct is booked once
// per time slot regardless of how many zones that slot has (see the group's
// uberCounts/uberStatuses below), so it never lives on a zone.
const getCount = (zone, dayName) => Number(zone.counts?.[dayName]) || 0;
const getStatus = (zone, dayName) => zone.statuses?.[dayName] || "draft";
const zoneDayIsPublished = (zone, dayName) => getStatus(zone, dayName) === "published";
const zoneHasPublished = (zone) =>
  Object.values(zone.statuses || {}).some((s) => s === "published");

// The time slot's single Uber Direct block — one count/status per day,
// independent of the zones scheduled at that same time.
const getUberCount = (group, dayName) => Number(group.uberCounts?.[dayName]) || 0;
const getUberCreatedCount = (group, dayName) => Number(group.uberCreatedCounts?.[dayName]) || 0;
const getUberStatus = (group, dayName) => group.uberStatuses?.[dayName] || "draft";
const uberDayIsPublished = (group, dayName) => getUberStatus(group, dayName) === "published";
const uberHasPublished = (group) =>
  Object.values(group.uberStatuses || {}).some((s) => s === "published");

const getZoneCreatedCount = (zone, dayName) => Number(zone.createdCounts?.[dayName]) || 0;
const zoneHasNames = (zone) => Array.isArray(zone.zoneNames) && zone.zoneNames.length > 0;
const zoneHasCounts = (zone) => DAY_NAMES.some((d) => getCount(zone, d) > 0);
const zoneHasCreated = (zone) => DAY_NAMES.some((d) => getZoneCreatedCount(zone, d) > 0);
const groupHasUber = (group) => DAY_NAMES.some((d) => getUberCount(group, d) > 0);
const groupHasCreatedUber = (group) => DAY_NAMES.some((d) => getUberCreatedCount(group, d) > 0);
const groupHasBtShifts = (group) => group.zones.some((z) => zoneHasNames(z) && zoneHasCounts(z));
const groupHasCreatedBt = (group) => group.zones.some(zoneHasCreated);

const rowCreatedCount = (row) => {
  const stored = Number(row.createdCount);
  if (Number.isFinite(stored)) return stored;
  return row.status === "published" ? Number(row.noOfShifts) || 0 : 0;
};

const emptyZone = () => ({ id: uid("zone"), zoneNames: [], counts: {}, statuses: {}, createdCounts: {} });
const emptyGroup = (startTime = "7:00 AM") => ({
  id: uid("group"),
  startTime,
  zones: [emptyZone()],
  uberCounts: {},
  uberStatuses: {},
  uberCreatedCounts: {},
});

// Pivots the week's per-day schedule docs into editable time-group blocks:
// one block per distinct start time, holding one row per zone (BT count only)
// plus a single group-level Uber Direct block shared by every zone in that slot.
const buildWeekGrid = (dayTabs, hubSchedules) => {
  const groupsByTime = new Map();
  const templateVotes = {};

  const getOrCreateGroup = (startTime) => {
    if (!groupsByTime.has(startTime)) {
      groupsByTime.set(startTime, {
        id: uid("group"),
        startTime,
        zones: new Map(),
        uberCounts: {},
        uberStatuses: {},
        uberCreatedCounts: {},
      });
    }
    return groupsByTime.get(startTime);
  };

  dayTabs.forEach((day) => {
    const schedule = getScheduleForDate(hubSchedules, toDateString(day.date));
    (schedule?.rows || []).forEach((row) => {
      const startTime = row.startTime || "";
      const rowTemplateId = row.shiftTemplateId?._id || row.shiftTemplateId || "";
      if (rowTemplateId) {
        templateVotes[rowTemplateId] = (templateVotes[rowTemplateId] || 0) + 1;
      }

      const group = getOrCreateGroup(startTime);

      if (String(row.routeType || SERVER_ROUTE_TYPE_BT).toUpperCase() === SERVER_ROUTE_TYPE_UBER) {
        // Merge rather than overwrite: older data saved one Uber Direct row
        // per zone, so a time slot can still load with several to combine.
        group.uberCounts[day.name] = (group.uberCounts[day.name] || 0) + (Number(row.noOfShifts) || 0);
        group.uberCreatedCounts[day.name] = (group.uberCreatedCounts[day.name] || 0) + rowCreatedCount(row);
        group.uberStatuses[day.name] =
          row.status === "published" || group.uberStatuses[day.name] === "published"
            ? "published"
            : row.status || group.uberStatuses[day.name] || "draft";
        return;
      }

      const zoneNames = Array.isArray(row.zoneNames) && row.zoneNames.length
        ? row.zoneNames
        : (row.zoneName || row.zone ? [row.zoneName || row.zone] : []);
      if (!zoneNames.length) return;

      // Rows can bundle multiple zones together (shared count across the
      // bundle), so a "zone row" is keyed by the whole zone set, not a
      // single zone name.
      const key = zoneKey(zoneNames);
      if (!group.zones.has(key)) {
        group.zones.set(key, { id: uid("zone"), zoneNames: [...zoneNames].sort(), counts: {}, statuses: {}, createdCounts: {} });
      }
      const zone = group.zones.get(key);
      zone.counts[day.name] = Number(row.noOfShifts) || 0;
      zone.statuses[day.name] = row.status || "draft";
      zone.createdCounts = zone.createdCounts || {};
      zone.createdCounts[day.name] = rowCreatedCount(row);
    });
  });

  let templateId = "";
  const votedEntries = Object.entries(templateVotes);
  if (votedEntries.length) {
    templateId = votedEntries.sort((a, b) => b[1] - a[1])[0][0];
  }

  const groups = Array.from(groupsByTime.values())
    .map((g) => ({ ...g, zones: Array.from(g.zones.values()) }))
    .sort((a, b) => parseTimeToMinutes(a.startTime) - parseTimeToMinutes(b.startTime));

  return { groups, templateId };
};

const groupHasPublished = (group) => group.zones.some(zoneHasPublished) || uberHasPublished(group);

// Coverage = shifts already created (status: published) vs. the total
// planned that day/week. Derived entirely from data already on each row —
// no separate "target" field exists or is needed.
const coveragePct = (created, total) => (total > 0 ? Math.round((created / total) * 100) : null);

const isOverbooked = (created, total) => created > total;

const coverageTone = (pct, created = 0, total = 0) => {
  if (isOverbooked(created, total)) return "over";
  if (pct === null || Number.isNaN(pct)) return "neutral";
  if (pct >= 100) return "good";
  if (pct >= 80) return "warn";
  return "bad";
};

const coverageLabel = (pct, created = 0, total = 0) => {
  if (isOverbooked(created, total)) return "Overbooked";
  if (pct === null || Number.isNaN(pct)) return "Nothing scheduled";
  if (pct >= 100) return "Fully staffed";
  if (pct >= 80) return "Nearly staffed";
  return "Understaffed";
};

// Sums created (published) vs. total (planned) shifts across a set of
// schedule-day docs — shared by the hub-pill %, day cards, and week footer.
// Sums created vs. planned shifts across schedule-day docs. createdCount is
// kept when the planned counter changes so coverage can show created vs. edited.
const sumCoverage = (rows) =>
  rows.reduce(
    (acc, r) => {
      acc.total += Number(r.noOfShifts) || 0;
      acc.created += rowCreatedCount(r);
      return acc;
    },
    { total: 0, created: 0 }
  );

const HubWeekGrid = ({
  hub,
  hubSchedules,
  dayTabs,
  selectedMetro,
  selectedMetroObj,
  onSaveSuccess,
  templateOptions,
  isReadOnly,
  isFetchingTemplates,
  upsertSchedule,
  isSaving,
  zoneOptions,
  isFetchingZones,
  weekOffset,
  weekLabel,
  lastWeekLabel,
  lastWeekDayTabs,
  lastWeekHubSchedules,
  isFetchingLastWeekSchedules,
}) => {
  const buildInitial = () => buildWeekGrid(dayTabs, hubSchedules);

  const [groups, setGroups] = useState(() => buildInitial().groups);
  const [sharedTemplateId, setSharedTemplateId] = useState(() => buildInitial().templateId);
  const [isDirty, setIsDirty] = useState(false);
  const [validationError, setValidationError] = useState("");
  const [pendingCopyLastWeek, setPendingCopyLastWeek] = useState(null);

  const canEdit = !isReadOnly;

  // Computed from the server's own data (not the in-progress edit state), so
  // it reflects what's actually been created, not what's staged locally.
  const weekCoverage = useMemo(() => {
    let total = 0;
    let created = 0;
    let daysBelowTarget = 0;
    let daysOverbooked = 0;
    dayTabs.forEach((day) => {
      const schedule = getScheduleForDate(hubSchedules, toDateString(day.date));
      const dayTotals = sumCoverage(schedule?.rows || []);
      total += dayTotals.total;
      created += dayTotals.created;
      if (dayTotals.created > dayTotals.total) daysOverbooked += 1;
      else if (dayTotals.total > 0 && dayTotals.created < dayTotals.total) daysBelowTarget += 1;
    });
    return { total, created, pct: coveragePct(created, total), daysBelowTarget, daysOverbooked };
  }, [dayTabs, hubSchedules]);

  useEffect(() => {
    const initial = buildWeekGrid(dayTabs, hubSchedules);
    setGroups(initial.groups);
    setSharedTemplateId(initial.templateId);
    setIsDirty(false);
    setValidationError("");
    // dayTabs is derived fresh each render from weekOffset; hub?.id + the
    // underlying schedules are what should actually trigger a rebuild.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hub?.id, hubSchedules]);

  const markDirty = () => setIsDirty(true);

  const updateGroupTime = (groupId, value) => {
    markDirty();
    setGroups((prev) => prev.map((g) => (g.id === groupId ? { ...g, startTime: value } : g)));
  };

  const updateZoneNames = (groupId, zoneId, value) => {
    markDirty();
    setGroups((prev) =>
      prev.map((g) =>
        g.id !== groupId
          ? g
          : { ...g, zones: g.zones.map((z) => (z.id === zoneId ? { ...z, zoneNames: value } : z)) }
      )
    );
  };

  const updateZoneCount = (groupId, zoneId, dayName, delta) => {
    markDirty();
    setGroups((prev) =>
      prev.map((g) => {
        if (g.id !== groupId) return g;
        return {
          ...g,
          zones: g.zones.map((z) => {
            if (z.id !== zoneId) return z;
            const current = getCount(z, dayName);
            return {
              ...z,
              counts: { ...z.counts, [dayName]: Math.max(0, current + delta) },
              statuses: { ...z.statuses, [dayName]: "draft" },
            };
          }),
        };
      })
    );
  };

  const updateUberCount = (groupId, dayName, delta) => {
    markDirty();
    setGroups((prev) =>
      prev.map((g) => {
        if (g.id !== groupId) return g;
        const current = getUberCount(g, dayName);
        return {
          ...g,
          uberCounts: { ...g.uberCounts, [dayName]: Math.max(0, current + delta) },
          uberStatuses: { ...g.uberStatuses, [dayName]: "draft" },
        };
      })
    );
  };

  const copyZoneMondayToWeek = (groupId, zoneId) => {
    markDirty();
    setGroups((prev) =>
      prev.map((g) => {
        if (g.id !== groupId) return g;
        return {
          ...g,
          zones: g.zones.map((z) => {
            if (z.id !== zoneId) return z;
            const mondayCount = getCount(z, "Monday");
            const counts = { ...z.counts };
            const statuses = { ...z.statuses };
            DAY_NAMES.forEach((d) => {
              const day = dayTabs.find((dt) => dt.name === d);
              if (day && isScheduleSlotLocked(day.date, g.startTime)) return;
              counts[d] = mondayCount;
              statuses[d] = "draft";
            });
            return { ...z, counts, statuses };
          }),
        };
      })
    );
  };

  const copyUberMondayToWeek = (groupId) => {
    markDirty();
    setGroups((prev) =>
      prev.map((g) => {
        if (g.id !== groupId) return g;
        const mondayCount = getUberCount(g, "Monday");
        const uberCounts = { ...g.uberCounts };
        const uberStatuses = { ...g.uberStatuses };
        DAY_NAMES.forEach((d) => {
          const day = dayTabs.find((dt) => dt.name === d);
          if (day && isScheduleSlotLocked(day.date, g.startTime)) return;
          uberCounts[d] = mondayCount;
          uberStatuses[d] = "draft";
        });
        return { ...g, uberCounts, uberStatuses };
      })
    );
  };

  // Snapshots every currently-locked cell (past days, or within the 2-hour
  // edit cutoff) keyed by startTime + zone, so a wholesale copy can restore
  // them afterwards instead of silently overwriting already-started/published
  // shifts.
  const LOCKED_UBER_KEY = "__uber__";
  const snapshotLockedCells = () => {
    const snapshot = new Map();
    groups.forEach((g) => {
      DAY_NAMES.forEach((d) => {
        const day = dayTabs.find((dt) => dt.name === d);
        if (!day || !isScheduleSlotLocked(day.date, g.startTime)) return;
        if (!snapshot.has(g.startTime)) snapshot.set(g.startTime, new Map());
        const byKey = snapshot.get(g.startTime);

        if (!byKey.has(LOCKED_UBER_KEY)) byKey.set(LOCKED_UBER_KEY, { zoneNames: null, cells: {} });
        byKey.get(LOCKED_UBER_KEY).cells[d] = { count: getUberCount(g, d), status: getUberStatus(g, d) };

        g.zones.forEach((z) => {
          if (!zoneHasNames(z)) return;
          const key = zoneKey(z.zoneNames);
          if (!byKey.has(key)) byKey.set(key, { zoneNames: z.zoneNames, cells: {} });
          byKey.get(key).cells[d] = { count: getCount(z, d), status: getStatus(z, d) };
        });
      });
    });
    return snapshot;
  };

  const handleCopyLastWeek = () => {
    if (!canEdit || !hub) return;
    const { groups: copiedGroups, templateId: copiedTemplateId } = buildWeekGrid(
      lastWeekDayTabs,
      lastWeekHubSchedules
    );
    if (!copiedGroups.length) {
      setValidationError(`No schedule found for last week (${lastWeekLabel || "prior week"}) to copy.`);
      return;
    }
    setPendingCopyLastWeek({ copiedGroups, copiedTemplateId });
  };

  const confirmCopyLastWeek = () => {
    if (!pendingCopyLastWeek) return;
    const { copiedGroups, copiedTemplateId } = pendingCopyLastWeek;
    setPendingCopyLastWeek(null);

    const lockedByTime = snapshotLockedCells();

    const mergedGroups = copiedGroups.map((g) => {
      const locked = lockedByTime.get(g.startTime);
      lockedByTime.delete(g.startTime);

      const zones = g.zones.map((z) => {
        const lockedCells = locked?.get(zoneKey(z.zoneNames))?.cells;
        const counts = {};
        const statuses = {};
        DAY_NAMES.forEach((d) => {
          const day = dayTabs.find((dt) => dt.name === d);
          if (day && isScheduleSlotLocked(day.date, g.startTime)) {
            if (lockedCells?.[d]) {
              counts[d] = lockedCells[d].count;
              statuses[d] = lockedCells[d].status;
            }
          } else {
            counts[d] = getCount(z, d);
            statuses[d] = "draft";
          }
        });
        return { id: uid("zone"), zoneNames: z.zoneNames, counts, statuses, createdCounts: {} };
      });

      const lockedUberCells = locked?.get(LOCKED_UBER_KEY)?.cells;
      const uberCounts = {};
      const uberStatuses = {};
      DAY_NAMES.forEach((d) => {
        const day = dayTabs.find((dt) => dt.name === d);
        if (day && isScheduleSlotLocked(day.date, g.startTime)) {
          if (lockedUberCells?.[d]) {
            uberCounts[d] = lockedUberCells[d].count;
            uberStatuses[d] = lockedUberCells[d].status;
          }
        } else {
          uberCounts[d] = getUberCount(g, d);
          uberStatuses[d] = "draft";
        }
      });

      return { id: uid("group"), startTime: g.startTime, zones, uberCounts, uberStatuses, uberCreatedCounts: {} };
    });

    // Any locked time slot that last week's copy doesn't touch at all must
    // still survive the replace, or an already-started/published shift this
    // week would silently disappear.
    lockedByTime.forEach((byKey, startTime) => {
      const zones = [];
      byKey.forEach((entry, key) => {
        if (key === LOCKED_UBER_KEY) return;
        const counts = {};
        const statuses = {};
        Object.entries(entry.cells).forEach(([d, cell]) => {
          counts[d] = cell.count;
          statuses[d] = cell.status;
        });
        zones.push({ id: uid("zone"), zoneNames: entry.zoneNames, counts, statuses, createdCounts: {} });
      });
      const uberEntry = byKey.get(LOCKED_UBER_KEY);
      const uberCounts = {};
      const uberStatuses = {};
      if (uberEntry) {
        Object.entries(uberEntry.cells).forEach(([d, cell]) => {
          uberCounts[d] = cell.count;
          uberStatuses[d] = cell.status;
        });
      }
      mergedGroups.push({
        id: uid("group"),
        startTime,
        zones: zones.length ? zones : [emptyZone()],
        uberCounts,
        uberStatuses,
        uberCreatedCounts: {},
      });
    });

    setGroups(mergedGroups);
    if (copiedTemplateId) setSharedTemplateId(copiedTemplateId);
    markDirty();
    setValidationError("");
  };

  const addGroup = () => {
    markDirty();
    setGroups((prev) => [...prev, emptyGroup()]);
  };

  const removeGroup = (groupId) => {
    markDirty();
    setGroups((prev) => prev.filter((g) => g.id !== groupId));
  };

  const addZone = (groupId) => {
    markDirty();
    setGroups((prev) =>
      prev.map((g) => (g.id === groupId ? { ...g, zones: [...g.zones, emptyZone()] } : g))
    );
  };

  const removeZone = (groupId, zoneId) => {
    markDirty();
    setGroups((prev) =>
      prev
        .map((g) => (g.id === groupId ? { ...g, zones: g.zones.filter((z) => z.id !== zoneId) } : g))
        .filter((g) => g.zones.length > 0)
    );
  };

  const handleTemplateChange = (value) => {
    markDirty();
    setSharedTemplateId(value);
  };

  const handleDiscard = () => {
    const initial = buildWeekGrid(dayTabs, hubSchedules);
    setGroups(initial.groups);
    setSharedTemplateId(initial.templateId);
    setIsDirty(false);
    setValidationError("");
  };

  const handleSave = async () => {
    if (!selectedMetroObj || !hub) return;
    setValidationError("");

    const savableGroups = groups.filter(
      (g) => groupHasUber(g) || groupHasBtShifts(g) || groupHasCreatedUber(g) || groupHasCreatedBt(g)
    );
    if (!savableGroups.length) {
      setValidationError("Add Uber Direct or zone shifts to at least one time slot before saving.");
      return;
    }
    const missingTime = savableGroups.some((g) => !String(g.startTime || "").trim());
    if (missingTime) {
      setValidationError("Every time slot needs a time.");
      return;
    }
    const missingZone = savableGroups.some((g) =>
      g.zones.some((z) => zoneHasCounts(z) && !zoneHasNames(z))
    );
    if (missingZone) {
      setValidationError("Every shift row with a count needs at least one zone selected.");
      return;
    }
    if (!sharedTemplateId) {
      setValidationError("Select a shift template for the week before saving.");
      return;
    }

    try {
      await Promise.all(
        dayTabs.map((day) => {
          const dateStr = toDateString(day.date);
          const rows = [];
          savableGroups.forEach((g) => {
            const uberCount = getUberCount(g, day.name);
            const uberCreated = getUberCreatedCount(g, day.name);
            // Keep a 0-count row when shifts were already created so coverage
            // can show created/planned as overbooked (e.g. 2/0) until they are deleted.
            if (uberCount > 0 || uberCreated > 0) {
              rows.push({
                rowId: `row_${dateStr}_${rows.length + 1}`,
                zoneId: "",
                zoneIds: [],
                zoneName: "",
                zoneNames: [],
                startTime: g.startTime,
                noOfShifts: uberCount,
                routeType: SERVER_ROUTE_TYPE_UBER,
                shiftTemplateId: sharedTemplateId,
                status: getUberStatus(g, day.name),
              });
            }
            g.zones.forEach((z) => {
              const count = getCount(z, day.name);
              const created = getZoneCreatedCount(z, day.name);
              if (count <= 0 && created <= 0) return;
              const zoneNames = Array.isArray(z.zoneNames) ? z.zoneNames : [];
              const zoneIds = zoneNames.map((zoneName) => `z_${selectedMetro}_${hub.name}_${zoneName}`);
              rows.push({
                rowId: `row_${dateStr}_${rows.length + 1}`,
                zoneId: zoneIds[0] || "",
                zoneIds,
                zoneName: zoneNames.join(", "),
                zoneNames,
                startTime: g.startTime,
                noOfShifts: count,
                routeType: SERVER_ROUTE_TYPE_BT,
                shiftTemplateId: sharedTemplateId,
                status: getStatus(z, day.name),
              });
            });
          });
          const d = new Date(day.date);
          d.setHours(0, 0, 0, 0);
          const totalCapacity = rows.reduce((sum, r) => sum + (Number(r.noOfShifts) || 0), 0);
          return upsertSchedule({
            metroId: selectedMetroObj._id,
            hub: hub.id,
            scheduleDate: d.toISOString(),
            totalCapacity,
            rows,
          }).unwrap();
        })
      );
      setIsDirty(false);
      onSaveSuccess?.();
    } catch (err) {
      console.error("Save week schedule error:", err);
      setValidationError("Something went wrong while saving. Please try again.");
    }
  };

  return (
    <div className="scheduling-assistant-page__week-grid">
      <datalist id={TIME_SUGGESTIONS_ID}>
        {TIME_SUGGESTIONS.map((t) => (
          <option key={t} value={t} />
        ))}
      </datalist>
      <div className="scheduling-assistant-page__shift-template">
        <div className="scheduling-assistant-page__shift-template-icon">
          <FaLayerGroup />
        </div>
        <div className="scheduling-assistant-page__shift-template-field">
          <label htmlFor="shift-template-select">Shift Template</label>
          <div className="scheduling-assistant-page__shift-template-select-row">
            <select
              id="shift-template-select"
              value={sharedTemplateId}
              onChange={(e) => handleTemplateChange(e.target.value)}
              disabled={isFetchingTemplates || isReadOnly}
              className={!sharedTemplateId && validationError ? "is-invalid" : ""}
            >
              <option value="">
                {isFetchingTemplates ? "Loading..." : "Select template..."}
              </option>
              {templateOptions.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.title}
                </option>
              ))}
            </select>
            <span className="scheduling-assistant-page__shift-template-caption">
              Applies to the whole week
            </span>
          </div>
        </div>
        <span className="scheduling-assistant-page__shift-template-hint">
          Each zone carries its own BT shift count; Uber Direct is booked once per time slot · arrow copies Mon → week
        </span>
        {canEdit && (
          <Button
            variant="outline-secondary"
            size="sm"
            className="scheduling-assistant-page__copy-last-week"
            onClick={handleCopyLastWeek}
            disabled={isFetchingLastWeekSchedules}
            title={`Copy ${hub?.name || "this hub"}'s entire schedule from ${lastWeekLabel || "last week"} into ${weekLabel || "this week"}`}
          >
            {isFetchingLastWeekSchedules ? (
              <>
                <FaSpinner className="scheduling-assistant-page__spin" /> Loading last week...
              </>
            ) : (
              <>
                <FaCopy /> Copy Last Week
              </>
            )}
          </Button>
        )}
      </div>
      {pendingCopyLastWeek && (
        <ConfirmDialog
          title="Copy Last Week's Schedule"
          bodyText={`Copy ${hub?.name || "this hub"}'s schedule from ${lastWeekLabel || "last week"} into ${weekLabel || "this week"}? Locked or already-started time slots will be kept as-is.`}
          isOpen
          confirmAction={confirmCopyLastWeek}
          onClose={() => setPendingCopyLastWeek(null)}
        />
      )}
      {groups.map((group) => {
        const zoneCount = group.zones.length;
        const btTotal = group.zones.reduce(
          (sum, z) => sum + DAY_NAMES.reduce((s, d) => s + getCount(z, d), 0),
          0
        );
        const uberTotal = DAY_NAMES.reduce((sum, d) => sum + getUberCount(group, d), 0);
        const weekTotal = btTotal + uberTotal;
        const lockGroup = groupHasPublished(group);
        return (
          <div key={group.id} className="scheduling-assistant-page__time-group">
            <div className="scheduling-assistant-page__time-group-header">
              <div className="scheduling-assistant-page__time-group-time">
                <FaRegClock className="scheduling-assistant-page__time-group-time-icon" />
                <input
                  type="text"
                  list={TIME_SUGGESTIONS_ID}
                  value={group.startTime}
                  onChange={(e) => updateGroupTime(group.id, e.target.value)}
                  onBlur={(e) => updateGroupTime(group.id, normalizeTimeInput(e.target.value))}
                  disabled={!canEdit}
                  autoComplete="off"
                />
              </div>
              <span className="scheduling-assistant-page__time-group-summary">
                {zoneCount} zone{zoneCount !== 1 ? "s" : ""} · {weekTotal} shift{weekTotal !== 1 ? "s" : ""}/week
                {" "}(BT {btTotal} · Uber Direct {uberTotal})
              </span>
              {canEdit && (
                <div className="scheduling-assistant-page__time-group-actions">
                  <Button variant="outline-primary" size="sm" onClick={() => addZone(group.id)}>
                    <FaPlus /> Add zone at {group.startTime || "this time"}
                  </Button>
                  <button
                    type="button"
                    className="scheduling-assistant-page__delete-row"
                    onClick={() => removeGroup(group.id)}
                    disabled={lockGroup}
                    aria-label="Delete time slot"
                    title={lockGroup ? "Contains published shifts" : "Delete time slot"}
                  >
                    <FaTrashAlt />
                  </button>
                </div>
              )}
            </div>

            <div className="scheduling-assistant-page__uber-row">
              <div className="scheduling-assistant-page__uber-row-label">
                <div className="scheduling-assistant-page__uber-row-text">
                  <span className="scheduling-assistant-page__uber-row-title">Uber block</span>
                  <span className="scheduling-assistant-page__uber-row-caption">One per day · all zones</span>
                </div>
                {canEdit && (
                  <button
                    type="button"
                    className="scheduling-assistant-page__copy-week"
                    onClick={() => copyUberMondayToWeek(group.id)}
                    aria-label="Copy Monday's Uber Direct count to the rest of the week"
                    title="Copy Monday's Uber Direct count to the rest of the week"
                  >
                    <FaArrowRight />
                  </button>
                )}
              </div>
              <div className="scheduling-assistant-page__zone-row-days">
                {dayTabs.map((day) => {
                  const cellLocked = !canEdit || isScheduleSlotLocked(day.date, group.startTime);
                  const count = getUberCount(group, day.name);
                  return (
                    <div className="scheduling-assistant-page__day-cell" key={day.name}>
                      <div
                        className="scheduling-assistant-page__stepper scheduling-assistant-page__stepper--uber"
                        data-intensity={Math.min(count, 6)}
                      >
                        <span className="scheduling-assistant-page__stepper-tag">UB</span>
                        <button
                          type="button"
                          onClick={() => updateUberCount(group.id, day.name, -1)}
                          disabled={cellLocked || count <= 0}
                          aria-label={`Decrease ${day.name} Uber Direct shift count`}
                        >
                          <FaMinus />
                        </button>
                        <span className="scheduling-assistant-page__stepper-value">{count}</span>
                        <button
                          type="button"
                          onClick={() => updateUberCount(group.id, day.name, 1)}
                          disabled={cellLocked}
                          aria-label={`Increase ${day.name} Uber Direct shift count`}
                        >
                          <FaPlus />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {group.zones.map((zone) => {
              const usedZones = group.zones
                .filter((z) => z.id !== zone.id)
                .flatMap((z) => z.zoneNames || []);
              const zoneLocked = zoneHasPublished(zone);
              const availableZoneOptions = zoneOptions.filter(
                (z) => (zone.zoneNames || []).includes(z) || !usedZones.includes(z)
              );
              return (
                <div className="scheduling-assistant-page__zone-row" key={zone.id}>
                  <div className="scheduling-assistant-page__zone-row-name">
                    <span className="scheduling-assistant-page__zone-row-connector" aria-hidden="true">
                      &#8627;
                    </span>
                    <ZoneMultiSelect
                      value={zone.zoneNames || []}
                      onChange={(val) => updateZoneNames(group.id, zone.id, val)}
                      disabled={!canEdit || isFetchingZones || zoneLocked}
                      hasError={zoneHasCounts(zone) && !zoneHasNames(zone)}
                      zoneOptions={availableZoneOptions}
                      isLoading={isFetchingZones}
                    />
                    {canEdit && (
                      <button
                        type="button"
                        className="scheduling-assistant-page__copy-week"
                        onClick={() => copyZoneMondayToWeek(group.id, zone.id)}
                        aria-label="Copy Monday's BT count to the rest of the week"
                        title="Copy Monday's BT count to the rest of the week"
                      >
                        <FaArrowRight />
                      </button>
                    )}
                    {canEdit && (
                      <button
                        type="button"
                        className="scheduling-assistant-page__delete-row"
                        onClick={() => removeZone(group.id, zone.id)}
                        disabled={zoneLocked}
                        aria-label="Remove zone"
                        title={zoneLocked ? "Contains published shifts" : "Remove zone"}
                      >
                        <FaTrashAlt />
                      </button>
                    )}
                  </div>
                  <div className="scheduling-assistant-page__zone-row-days">
                    {dayTabs.map((day) => {
                      const cellLocked = !canEdit || isScheduleSlotLocked(day.date, group.startTime);
                      const count = getCount(zone, day.name);
                      return (
                        <div className="scheduling-assistant-page__day-cell" key={day.name}>
                          <div
                            className="scheduling-assistant-page__stepper scheduling-assistant-page__stepper--bt"
                            data-intensity={Math.min(count, 6)}
                          >
                            <span className="scheduling-assistant-page__stepper-tag">BT</span>
                            <button
                              type="button"
                              onClick={() => updateZoneCount(group.id, zone.id, day.name, -1)}
                              disabled={cellLocked || count <= 0}
                              aria-label={`Decrease ${day.name} BT shift count`}
                            >
                              <FaMinus />
                            </button>
                            <span className="scheduling-assistant-page__stepper-value">{count}</span>
                            <button
                              type="button"
                              onClick={() => updateZoneCount(group.id, zone.id, day.name, 1)}
                              disabled={cellLocked}
                              aria-label={`Increase ${day.name} BT shift count`}
                            >
                              <FaPlus />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}

      {weekOffset >= 0 && (
        <Button
          variant="outline-secondary"
          className="scheduling-assistant-page__add-time"
          onClick={addGroup}
        >
          <FaPlus /> Add Time Slot
        </Button>
      )}

      {validationError && (
        <div className="scheduling-assistant-page__validation-msg">
          <FaExclamationTriangle />
          <span>{validationError}</span>
        </div>
      )}

      {weekOffset >= 0 && (
        <div className="scheduling-assistant-page__footer">
          {weekCoverage.total > 0 || weekCoverage.created > 0 ? (
            <div className="scheduling-assistant-page__week-coverage">
              <span className="scheduling-assistant-page__week-coverage-label">Week Coverage</span>
              <span className="scheduling-assistant-page__week-coverage-value">
                {weekCoverage.created} / {weekCoverage.total} shifts
                {weekCoverage.pct !== null ? ` · ${weekCoverage.pct}%` : ""}
              </span>
              {weekCoverage.daysOverbooked > 0 ? (
                <span className="scheduling-assistant-page__week-coverage-chip scheduling-assistant-page__week-coverage-chip--over">
                  {weekCoverage.daysOverbooked} day{weekCoverage.daysOverbooked !== 1 ? "s" : ""} overbooked
                </span>
              ) : weekCoverage.daysBelowTarget === 0 ? (
                <span className="scheduling-assistant-page__week-coverage-chip scheduling-assistant-page__week-coverage-chip--good">
                  ✓ All days meet target
                </span>
              ) : (
                <span className="scheduling-assistant-page__week-coverage-chip scheduling-assistant-page__week-coverage-chip--warn">
                  {weekCoverage.daysBelowTarget} day{weekCoverage.daysBelowTarget !== 1 ? "s" : ""} below target
                </span>
              )}
            </div>
          ) : null}
          <div className="scheduling-assistant-page__footer-right">
            <span className="scheduling-assistant-page__footer-status">
              {isDirty ? (
                <>
                  <span className="scheduling-assistant-page__unsaved-dot" />
                  Unsaved changes
                </>
              ) : (
                "All changes saved"
              )}
            </span>
            <div className="scheduling-assistant-page__footer-actions">
              <Button variant="outline-secondary" onClick={handleDiscard} disabled={!isDirty || isSaving}>
                Discard
              </Button>
              <Button variant="primary" onClick={handleSave} disabled={isSaving}>
                {isSaving ? (
                  <>
                    <FaSpinner className="scheduling-assistant-page__spin" /> Saving...
                  </>
                ) : (
                  <>
                    <FaSave /> Save Week
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Scheduling = () => {
  const [selectedMetro, setSelectedMetro] = useState("");
  const [visibleHubIds, setVisibleHubIds] = useState([]);
  const [activeHubId, setActiveHubId] = useState("");

  const [weekOffset, setWeekOffset] = useState(0);
  // Only past weeks (weekOffset < 0) are read-only. Current and future weeks allow editing.
  const isReadOnly = weekOffset < 0;

  const currentUser = useSelector((state) => state.auth.currentUser);
  const { data: metros, isFetching: isFetchingMetros } = useGetMetrosQuery(null, {
    refetchOnMountOrArgChange: true,
  });
  const { data: stores, isFetching: isFetchingStores } = useGetStoresQuery(null, {
    refetchOnMountOrArgChange: true,
  });
  const { data: shiftTemplates, isFetching: isFetchingTemplates } = useGetShiftTemplatesQuery(null, {
    refetchOnMountOrArgChange: true,
  });

  const selectedMetroObj = useMemo(() => {
    if (!Array.isArray(metros) || !selectedMetro) return null;
    return metros.find((m) => m?.name === selectedMetro);
  }, [metros, selectedMetro]);

  const { data: zonesData, isFetching: isFetchingZones } = useGetZonesQuery(
    selectedMetroObj?.code,
    { skip: !selectedMetroObj?.code, refetchOnMountOrArgChange: true }
  );

  const zoneOptions = useMemo(() => {
    const list = Array.isArray(zonesData?.zones) ? zonesData.zones : [];
    return list.map((z) => (typeof z === "string" ? z : z?.name)).filter(Boolean);
  }, [zonesData]);

  const metroTabs = useMemo(() => {
    if (["metro_manager", "dispatcher"].includes(currentUser?.role)) {
      const metroManagerMetros = JSON.parse(localStorage.getItem("user_metro") || "[]");
      return Array.isArray(metroManagerMetros) ? metroManagerMetros.filter(Boolean) : [];
    }
    if (!Array.isArray(metros)) return [];
    return metros.map((item) => item?.name).filter(Boolean);
  }, [metros, currentUser]);

  useEffect(() => {
    if (!metroTabs.length) return;
    if (!selectedMetro || !metroTabs.includes(selectedMetro)) {
      setSelectedMetro(metroTabs[0]);
    }
  }, [metroTabs, selectedMetro]);

  const monday = getWeekMonday(weekOffset);
  const weekLabel = `${monday.toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${new Date(monday.getTime() + 6 * 24 * 60 * 60 * 1000).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;
  const dayTabs = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return DAY_NAMES.map((name, i) => {
      const date = new Date(monday);
      date.setDate(monday.getDate() + i);
      return {
        name,
        label: date.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
        date,
        isToday: date.toDateString() === now.toDateString(),
      };
    });
  }, [monday]);

  const hubList = useMemo(() => {
    if (!Array.isArray(stores) || !selectedMetro) return [];
    return stores
      .filter((store) => store?.metro === selectedMetro)
      .map((store) => ({ id: store._id, name: store.name }))
      .filter((h) => h.name);
  }, [stores, selectedMetro]);

  const fromDate = useMemo(() => toDateString(monday), [monday]);
  const toDate = useMemo(() => {
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return toDateString(sunday);
  }, [monday]);

  // "Last week" relative to whatever week is currently selected, used by the
  // per-hub "Copy Last Week" action below.
  const lastWeekMonday = getWeekMonday(weekOffset - 1);
  const lastWeekLabel = `${lastWeekMonday.toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${new Date(lastWeekMonday.getTime() + 6 * 24 * 60 * 60 * 1000).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;
  const lastWeekDayTabs = useMemo(
    () =>
      DAY_NAMES.map((name, i) => {
        const date = new Date(lastWeekMonday);
        date.setDate(lastWeekMonday.getDate() + i);
        return { name, date };
      }),
    [lastWeekMonday]
  );
  const lastWeekFromDate = useMemo(() => toDateString(lastWeekMonday), [lastWeekMonday]);
  const lastWeekToDate = useMemo(() => {
    const sunday = new Date(lastWeekMonday);
    sunday.setDate(lastWeekMonday.getDate() + 6);
    return toDateString(sunday);
  }, [lastWeekMonday]);

  const { data: schedules = [], isFetching: isFetchingSchedules, refetch: refetchSchedules } = useGetSchedulesByMetroQuery(
    {
      metro: selectedMetroObj?._id,
      fromDate,
      toDate,
    },
    { skip: !selectedMetroObj?._id }
  );

  const { data: lastWeekSchedules = [], isFetching: isFetchingLastWeekSchedules } = useGetSchedulesByMetroQuery(
    {
      metro: selectedMetroObj?._id,
      fromDate: lastWeekFromDate,
      toDate: lastWeekToDate,
    },
    { skip: !selectedMetroObj?._id }
  );

  const [upsertSchedule, { isLoading: isSaving }] = useUpsertScheduleMutation();
  const [runShiftsFromSchedule, { isLoading: isGenerating }] = useRunShiftsFromScheduleMutation();
  const [generateMessage, setGenerateMessage] = useState(null);

  const isRootUser = currentUser?.role === "root";

  const handleGenerateShifts = async () => {
    if (!selectedMetro || !isRootUser) return;
    const confirmed = window.confirm(
      `Generate shifts now for ${selectedMetro}, ${weekLabel}? This creates live shift postings from the configuration below instead of waiting for the Wednesday auto-run.`
    );
    if (!confirmed) return;
    setGenerateMessage(null);
    try {
      const result = await runShiftsFromSchedule({
        metro: selectedMetro,
        fromDate,
        toDate,
      }).unwrap();
      setGenerateMessage({ type: "success", text: `Created ${result?.created ?? 0} new shift(s).` });
    } catch (err) {
      setGenerateMessage({
        type: "error",
        text: err?.data?.message || "Something went wrong generating shifts. Please try again.",
      });
    }
  };

  const schedulesByHub = useMemo(() => {
    const map = {};
    hubList.forEach((hub) => {
      map[hub.id] = { hub, schedules: [] };
    });
    (Array.isArray(schedules) ? schedules : []).forEach((s) => {
      const hubId = s.hub?._id || s.hub;
      if (hubId && map[hubId]) {
        map[hubId].schedules.push(s);
      }
    });
    return map;
  }, [schedules, hubList]);

  const lastWeekSchedulesByHub = useMemo(() => {
    const map = {};
    hubList.forEach((hub) => {
      map[hub.id] = [];
    });
    (Array.isArray(lastWeekSchedules) ? lastWeekSchedules : []).forEach((s) => {
      const hubId = s.hub?._id || s.hub;
      if (hubId && map[hubId]) {
        map[hubId].push(s);
      }
    });
    return map;
  }, [lastWeekSchedules, hubList]);

  // Whole-week created/total shifts per hub, for the hub pill's % + dot color.
  const hubCoverage = useMemo(() => {
    const map = {};
    Object.entries(schedulesByHub).forEach(([hubId, { schedules: hubSchedules }]) => {
      const allRows = hubSchedules.flatMap((s) => s.rows || []);
      const { total, created } = sumCoverage(allRows);
      map[hubId] = { total, created, pct: coveragePct(created, total) };
    });
    return map;
  }, [schedulesByHub]);

  const templateOptions = useMemo(() => {
    if (!Array.isArray(shiftTemplates)) return [];
    return shiftTemplates
      .filter((t) => !selectedMetro || t?.metro === selectedMetro)
      .map((t) => ({ id: t._id, title: t.title }))
      .filter((t) => t.title);
  }, [shiftTemplates, selectedMetro]);

  useEffect(() => {
    if (hubList.length > 0 && visibleHubIds.length === 0) {
      setVisibleHubIds([hubList[0].id]);
      setActiveHubId(hubList[0].id);
    }
  }, [hubList, selectedMetro]);

  const visibleHubs = useMemo(
    () => hubList.filter((h) => visibleHubIds.includes(h.id)),
    [hubList, visibleHubIds]
  );
  const availableHubsToAdd = useMemo(
    () => hubList.filter((h) => !visibleHubIds.includes(h.id)),
    [hubList, visibleHubIds]
  );
  const activeHub = useMemo(
    () => visibleHubs.find((h) => h.id === activeHubId) || visibleHubs[0] || null,
    [visibleHubs, activeHubId]
  );
  const addHub = (hubId) => {
    setVisibleHubIds((prev) => [...prev, hubId]);
    setActiveHubId(hubId);
  };
  const removeHub = (hubId) => {
    setVisibleHubIds((prev) => {
      const next = prev.filter((id) => id !== hubId);
      if (activeHubId === hubId) {
        setActiveHubId(next[0] || "");
      }
      return next;
    });
  };

  const activeHubSchedules = activeHub ? (schedulesByHub[activeHub.id] || { schedules: [] }).schedules : [];
  const activeHubLastWeekSchedules = activeHub ? lastWeekSchedulesByHub[activeHub.id] || [] : [];

  const activeCoverage = (activeHub && hubCoverage[activeHub.id]) || { total: 0, created: 0 };
  const hasPendingShifts = activeCoverage.total > activeCoverage.created;
  const generateBannerSubtitle = (() => {
    const { total, created } = activeCoverage;
    const cta = isRootUser ? ", then generate." : ".";
    if (created > total) {
      return `${created} live shift${created !== 1 ? "s" : ""} exceed the ${total} currently scheduled.`;
    }
    if (total === 0) return `Nothing scheduled for this week yet. Add time slots below${cta}`;
    if (created === 0) return `Nothing is live for this week yet. Review the configuration below${cta}`;
    if (created < total) {
      return isRootUser
        ? `${created} of ${total} shifts are live this week — generate to catch up the rest.`
        : `${created} of ${total} shifts are live this week.`;
    }
    return `All ${total} shifts are live for this week.`;
  })();

  return (
    <CardLayout>
      <div className="scheduling-assistant-page">
        {activeHub && (
          <div className="scheduling-assistant-page__generate-banner">
            <div className="scheduling-assistant-page__generate-banner-icon">
              <FaBolt />
            </div>
            <div className="scheduling-assistant-page__generate-banner-text">
              <strong>Shifts are created on demand</strong>
              <span>{generateBannerSubtitle}</span>
            </div>
            {isRootUser && (
              <Button
                variant="primary"
                onClick={handleGenerateShifts}
                disabled={isGenerating || !selectedMetro || !hasPendingShifts}
                className="scheduling-assistant-page__generate-banner-btn"
              >
                {isGenerating ? (
                  <>
                    <FaSpinner className="scheduling-assistant-page__spin" /> Generating...
                  </>
                ) : (
                  <>
                    <FaBolt /> Generate Shifts
                  </>
                )}
              </Button>
            )}
          </div>
        )}

        {generateMessage && (
          <div
            className={`scheduling-assistant-page__generate-msg scheduling-assistant-page__generate-msg--${generateMessage.type}`}
          >
            {generateMessage.type === "success" ? <FaBolt /> : <FaExclamationTriangle />}
            <span>{generateMessage.text}</span>
            <button
              type="button"
              className="scheduling-assistant-page__generate-msg-close"
              onClick={() => setGenerateMessage(null)}
              aria-label="Dismiss"
            >
              <FaTimes />
            </button>
          </div>
        )}

        <div className="scheduling-assistant-page__metro-header">
          <div className="scheduling-assistant-page__metro-heading-group">
            <div className="scheduling-assistant-page__metro-heading">
              <select
                className="scheduling-assistant-page__metro-select"
                value={selectedMetro}
                onChange={(e) => {
                  setSelectedMetro(e.target.value);
                  setVisibleHubIds([]);
                  setActiveHubId("");
                }}
                disabled={isFetchingMetros || !metroTabs.length}
                aria-label="Metro"
              >
                {!metroTabs.length ? (
                  <option value="">
                    {isFetchingMetros ? "Loading metros..." : "No metros available"}
                  </option>
                ) : (
                  metroTabs.map((metro) => (
                    <option key={metro} value={metro}>
                      {metro}
                    </option>
                  ))
                )}
              </select>
              <FaChevronDown className="scheduling-assistant-page__metro-select-caret" />
            </div>
            <p className="scheduling-assistant-page__metro-subtitle">
              Metro · {hubList.length} hub{hubList.length !== 1 ? "s" : ""}
            </p>
          </div>

          <div className="scheduling-assistant-page__hub-pills">
            {visibleHubs.map((hub) => {
              const isActive = activeHub?.id === hub.id;
              const coverage = hubCoverage[hub.id] || { pct: null, created: 0, total: 0 };
              const tone = coverageTone(coverage.pct, coverage.created, coverage.total);
              return (
                <div
                  key={hub.id}
                  className={`scheduling-assistant-page__hub-pill${isActive ? " active" : ""}`}
                  style={{ "--hub-dot-color": HUB_TONE_COLORS[tone] }}
                  role="button"
                  tabIndex={0}
                  onClick={() => setActiveHubId(hub.id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setActiveHubId(hub.id);
                    }
                  }}
                >
                  <span className="scheduling-assistant-page__hub-pill-dot" />
                  <span className="scheduling-assistant-page__hub-pill-name">{hub.name}</span>
                  {coverage.pct !== null && (
                    <span className="scheduling-assistant-page__hub-pill-pct">{coverage.pct}%</span>
                  )}
                  {visibleHubs.length > 1 && (
                    <button
                      type="button"
                      className="scheduling-assistant-page__hub-pill-remove"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeHub(hub.id);
                      }}
                      aria-label={`Remove ${hub.name}`}
                    >
                      <FaTimes />
                    </button>
                  )}
                </div>
              );
            })}
            <Dropdown onSelect={(hubId) => hubId && addHub(hubId)} disabled={availableHubsToAdd.length === 0}>
              <Dropdown.Toggle
                variant="outline-primary"
                size="sm"
                className="scheduling-assistant-page__hub-pill-add"
                aria-label="Add hub"
                title="Add hub"
              >
                <FaPlus />
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {availableHubsToAdd.map((hub) => (
                  <Dropdown.Item key={hub.id} eventKey={hub.id}>
                    {hub.name}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>

        <div className="scheduling-assistant-page__week-nav">
          <button
            type="button"
            aria-label="Previous week"
            onClick={() => setWeekOffset((w) => w - 1)}
          >
            <FaChevronLeft />
          </button>
          <span className="scheduling-assistant-page__week-nav-label">{weekLabel}</span>
          <button
            type="button"
            aria-label="Next week"
            onClick={() => setWeekOffset((w) => w + 1)}
          >
            <FaChevronRight />
          </button>
        </div>

        <div className="scheduling-assistant-page__day-strip">
          <div className="scheduling-assistant-page__day-strip-spacer">TIME · ZONE</div>
          <div className="scheduling-assistant-page__day-strip-days">
            {dayTabs.map((day) => {
              const schedule = activeHub ? getScheduleForDate(activeHubSchedules, toDateString(day.date)) : null;
              const { total: totalCount, created: createdCount } = sumCoverage(schedule?.rows || []);
              const pct = coveragePct(createdCount, totalCount);
              const tone = coverageTone(pct, createdCount, totalCount);
              const showBar = totalCount > 0 || createdCount > 0;
              return (
                <div
                  key={day.name}
                  className={`scheduling-assistant-page__day-card${day.isToday ? " today" : ""}${
                    isPastScheduleDay(day.date) ? " locked" : ""
                  }`}
                >
                  <span className="scheduling-assistant-page__day-card-name">{day.name.slice(0, 3).toUpperCase()}</span>
                  <span className="scheduling-assistant-page__day-card-date">{day.label}</span>
                  <span
                    className="scheduling-assistant-page__day-card-count"
                    title={`${createdCount} created of ${totalCount} scheduled`}
                  >
                    {createdCount}
                    <span className="scheduling-assistant-page__day-card-count-total">/{totalCount}</span>
                  </span>
                  {showBar && (
                    <>
                      <div className="scheduling-assistant-page__day-card-bar">
                        <div
                          className={`scheduling-assistant-page__day-card-bar-fill scheduling-assistant-page__day-card-bar-fill--${tone}`}
                          style={{ width: `${tone === "over" ? 100 : Math.min(pct || 0, 100)}%` }}
                        />
                      </div>
                      <span
                        className={`scheduling-assistant-page__day-card-status scheduling-assistant-page__day-card-status--${tone}`}
                      >
                        {coverageLabel(pct, createdCount, totalCount)}
                      </span>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {isReadOnly && (
          <div className="scheduling-assistant-page__readonly-banner">
            <FaLock />
            <span>This week is read-only. Navigate to a future week to make edits.</span>
          </div>
        )}

        {isFetchingSchedules && selectedMetroObj && (
          <div className="scheduling-assistant-page__loading">
            <FaSpinner className="scheduling-assistant-page__spin" />
            <span>Loading schedules...</span>
          </div>
        )}

        {!hubList.length && selectedMetro && !isFetchingStores && (
          <div className="scheduling-assistant-page__empty">
            <FaInbox />
            <span>No hubs found for {selectedMetro}. Add hubs in the Better Trucks section.</span>
          </div>
        )}
        {activeHub && (
          <HubWeekGrid
            hub={activeHub}
            hubSchedules={activeHubSchedules}
            dayTabs={dayTabs}
            selectedMetro={selectedMetro}
            selectedMetroObj={selectedMetroObj}
            onSaveSuccess={refetchSchedules}
            templateOptions={templateOptions}
            isReadOnly={isReadOnly}
            isFetchingTemplates={isFetchingTemplates}
            upsertSchedule={upsertSchedule}
            isSaving={isSaving}
            zoneOptions={zoneOptions}
            isFetchingZones={isFetchingZones}
            weekOffset={weekOffset}
            weekLabel={weekLabel}
            lastWeekLabel={lastWeekLabel}
            lastWeekDayTabs={lastWeekDayTabs}
            lastWeekHubSchedules={activeHubLastWeekSchedules}
            isFetchingLastWeekSchedules={isFetchingLastWeekSchedules}
          />
        )}
      </div>
    </CardLayout>
  );
  
};

export default Scheduling;
